import React, { createContext, FC, useCallback, useState, useEffect, useRef } from "react";
import ResourceFiltrationService from "@app/projects/resource-filtration/resourceFiltrationService";
import ExportService from "@app/exports/exportService";
import { LocationConfidenceScoresLabelEnum, NetworkAssessmentAnalysisType, PatientVolumeLabelEnum, RecommendedActionTypeEnum } from "@app/projects/enumerators";
import { AccuracySupplementalRowTypes, ExportFormatType, ReportSettingsType } from "@app/exports/enumerations";
import { toast } from "react-toastify";
import { EntityTypeFilter, ExportAccuracyActionableProvidersBulkModel, ExportRecommendedActionReasonModel, ReportSettingsModel } from "@app/exports/exportModels";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import ProjectService from "@app/projects/projectService";
import { AccuracyReportType } from "@app/reports/accuracy-reports/accuracyReportType";
import { AccuracyReportsWizardContextSteps, AccuracyProject, FrequencyOptionsType, frequencyOptions, combineDateTimeToISO } from "@app/reports/accuracy-reports/import/interfaces";
import { ReportFormatOptionsType, reportFormatOptions } from "@app/reports/volume-reports/import/interfaces";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";

interface AccuracyReportsContextProps {
  // State
  reportType: AccuracyReportType;
  page: AccuracyReportsWizardContextSteps;
  loading: boolean;
  processing: boolean;
  error: string;
  success: boolean;
  
  // Data
  projects: AccuracyProject[];
  selectedProjects: AccuracyProject[];
  
  // Report Config
  reportName: string;
  reportFormat: ReportFormatOptionsType;
  sharedUsers: number[];
  alertWhenReady: boolean;
  splitReport: boolean;
  scheduleReportCreation: boolean;
  startDate: string;
  startTime: string;
  frequency: FrequencyOptionsType;
  
  // Actionable Providers State
  entityTypeFilter: EntityTypeFilter;
  rowTypeIds: number[];
  recommendedActionIds: number[];
  recommendedActionReasons: ExportRecommendedActionReasonModel[];
  selectedRecommendedActionReasons: string[];
  showAllRecommendedActionReasons: boolean;
  reportSettingsList: ReportSettingsModel[];
  currentReportSettings: ReportSettingsModel | undefined;
  reportSettingsHasChanges: boolean;
  createReportSettingsNameInputDisabled: boolean;
  isSelectionValid: boolean;
  reportSettingsHasInvalidChanges: boolean;
  saveClicked: boolean;
  loadingRecommendedActionReasons: boolean;
  showSettingsPartialApplyWarning: boolean;
  
  // Actions
  setProjects: React.Dispatch<React.SetStateAction<AccuracyProject[]>>;
  setSelectedProjects: React.Dispatch<React.SetStateAction<AccuracyProject[]>>;
  setError: React.Dispatch<React.SetStateAction<string>>;
  setReportName: React.Dispatch<React.SetStateAction<string>>;
  setReportFormat: React.Dispatch<React.SetStateAction<ReportFormatOptionsType>>;
  setSharedUsers: React.Dispatch<React.SetStateAction<number[]>>;
  setAlertWhenReady: React.Dispatch<React.SetStateAction<boolean>>;
  setSplitReport: React.Dispatch<React.SetStateAction<boolean>>;
  setScheduleReportCreation: React.Dispatch<React.SetStateAction<boolean>>;
  setStartDate: React.Dispatch<React.SetStateAction<string>>;
  setStartTime: React.Dispatch<React.SetStateAction<string>>;
  setFrequency: React.Dispatch<React.SetStateAction<FrequencyOptionsType>>;
  refreshProjects: () => void;
  updateProjectVisibility: (filterString: string) => void;
  generateReports: () => Promise<void>;
  
  // Actionable Providers Actions
  setEntityTypeFilter: React.Dispatch<React.SetStateAction<EntityTypeFilter>>;
  setRowTypeIds: React.Dispatch<React.SetStateAction<number[]>>;
  setRecommendedActionIds: React.Dispatch<React.SetStateAction<number[]>>;
  setSelectedRecommendedActionReasons: React.Dispatch<React.SetStateAction<string[]>>;
  setShowAllRecommendedActionReasons: React.Dispatch<React.SetStateAction<boolean>>;
  setShowSettingsPartialApplyWarning: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentReportSettings: React.Dispatch<React.SetStateAction<ReportSettingsModel | undefined>>;
  setReportSettingsHasChanges: React.Dispatch<React.SetStateAction<boolean>>;
  setCreateReportSettingsNameInputDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSelectionValid: React.Dispatch<React.SetStateAction<boolean>>;
  setReportSettingsHasInvalidChanges: React.Dispatch<React.SetStateAction<boolean>>;
  setSaveClicked: React.Dispatch<React.SetStateAction<boolean>>;
  setReportSettingsList: React.Dispatch<React.SetStateAction<ReportSettingsModel[]>>;
  handleEntityTypeIncludeChangeDropdown: (selectedItems: DropdownItem<string>[]) => void;
  handleRowTypeChangeDropdown: (selectedItems: DropdownItem<number>[]) => void;
  handleRecommendedActionChangeDropdown: (ids: number[]) => void;
  handleShowAllReasonsChange: () => void;
  handleRecommendedActionReasonDropdownChange: (recommendedActionReasons: Array<DropdownItem<string>>) => void;
  loadReportSettings: () => Promise<ReportSettingsModel | undefined>;
  loadRecommendedActionReasons: (defaultSettings?: ReportSettingsModel) => Promise<void>;
  buildReportSettingsModel: (isDefault: boolean, name: string) => ReportSettingsModel;
  stringifyStateReportSettings: () => string;
  parseReportSettingsJsonToState: (reportSettingsJson: string) => void;
  
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  goToStep: (step: AccuracyReportsWizardContextSteps) => void;
}

interface AccuracyReportsProviderProps {
  reportType: AccuracyReportType;
  children: React.ReactNode;
}

export const AccuracyReportsContext = createContext<Partial<AccuracyReportsContextProps>>({});

export const AccuracyReportsProvider: FC<AccuracyReportsProviderProps> = ({ reportType, children }) => {
  const [page, setPage] = useState<AccuracyReportsWizardContextSteps>(AccuracyReportsWizardContextSteps.SelectProjects);
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState(false);
  
  const resourceFiltrationService = useRef(new ResourceFiltrationService()).current;
  const exportService = useRef(new ExportService()).current;
  const projectService = useRef(new ProjectService()).current;
  const recommendedActionReasonsLoadedRef = useRef(false);

  const [projects, setProjects] = useState<AccuracyProject[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<AccuracyProject[]>([]);
  
  const generateDefaultReportName = () => {
    const now = new Date();
    const dateStamp = now.toISOString().slice(0, 10).replace(/-/g, ''); 
    const timeStamp = now.toTimeString().slice(0, 8).replace(/:/g, ''); 
    return `AccuracyReport_${dateStamp}_${timeStamp}`;
  };
  
  const [reportName, setReportName] = useState<string>(generateDefaultReportName());
  const [reportFormat, setReportFormat] = useState<ReportFormatOptionsType>(reportFormatOptions[0]);
  const [sharedUsers, setSharedUsers] = useState<number[]>([]);
  const [alertWhenReady, setAlertWhenReady] = useState<boolean>(true);
  const [splitReport, setSplitReport] = useState<boolean>(true);
  const [scheduleReportCreation, setScheduleReportCreation] = useState<boolean>(false);
  const generateDefaultDateTime = () => {
    const now = new Date();
    const dateString = now.toISOString().slice(0, 10); // YYYY-MM-DD format
    const timeString = now.toTimeString().slice(0, 5); // HH:MM format
    return { date: dateString, time: timeString };
  };
  
  const defaultDateTime = generateDefaultDateTime();
  const [startDate, setStartDate] = useState<string>(defaultDateTime.date);
  const [startTime, setStartTime] = useState<string>(defaultDateTime.time);
  const [frequency, setFrequency] = useState<FrequencyOptionsType>(frequencyOptions[0]);

  // Actionable Providers State
  const getDefaultRecommendedActionIds = useCallback((): number[] => {
    const defaults: number[] = [];
    Object.values(RecommendedActionTypeEnum).forEach((v, i) => {
      if (v !== RecommendedActionTypeEnum.Unknown) {
        defaults.push(i);
      }
    });
    return defaults;
  }, []);

  const getDefaultRowTypeIds = useCallback((): number[] => {
    const defaults: number[] = [];
    Object.values(AccuracySupplementalRowTypes).forEach((v, i) => {
      if (v !== AccuracySupplementalRowTypes.Undefined && v !== AccuracySupplementalRowTypes.Unknown) {
        defaults.push(i);
      }
    });
    return defaults;
  }, []);

  const [entityTypeFilter, setEntityTypeFilter] = useState<EntityTypeFilter>({
    includePractitionerEntityType: true,
    includeServiceLocationEntityType: true,
  });
  const [rowTypeIds, setRowTypeIds] = useState<number[]>(getDefaultRowTypeIds());
  const [recommendedActionIds, setRecommendedActionIds] = useState<number[]>(getDefaultRecommendedActionIds());
  const [recommendedActionReasons, setRecommendedActionReasons] = useState<ExportRecommendedActionReasonModel[]>([]);
  const [selectedRecommendedActionReasons, setSelectedRecommendedActionReasons] = useState<string[]>([]);
  const [showAllRecommendedActionReasons, setShowAllRecommendedActionReasons] = useState<boolean>(true);
  const [loadingRecommendedActionReasons, setLoadingRecommendedActionReasons] = useState<boolean>(false);
  
  // Report Settings State
  const [reportSettingsList, setReportSettingsList] = useState<ReportSettingsModel[]>([]);
  const [currentReportSettings, setCurrentReportSettings] = useState<ReportSettingsModel | undefined>(undefined);
  const [reportSettingsHasChanges, setReportSettingsHasChanges] = useState<boolean>(false);
  const [createReportSettingsNameInputDisabled, setCreateReportSettingsNameInputDisabled] = useState<boolean>(true);
  const [isSelectionValid, setIsSelectionValid] = useState<boolean>(true);
  const [reportSettingsHasInvalidChanges, setReportSettingsHasInvalidChanges] = useState<boolean>(false);
  const [saveClicked, setSaveClicked] = useState<boolean>(false);
  const [showSettingsPartialApplyWarning, setShowSettingsPartialApplyWarning] = useState<boolean>(false);

  // Actionable Providers Handlers
  const handleEntityTypeIncludeChangeDropdown = useCallback((selectedItems: DropdownItem<string>[]) => {
    const practitionerSelected = !!selectedItems.find((item) => item.key === "practitioner");
    const serviceLocationSelected = !!selectedItems.find((item) => item.key === "servicelocation");
    setEntityTypeFilter({
      includePractitionerEntityType: practitionerSelected,
      includeServiceLocationEntityType: serviceLocationSelected,
    });
    setReportSettingsHasChanges(true);
  }, []);

  const handleRowTypeChangeDropdown = useCallback((selectedItems: DropdownItem<number>[]) => {
    const ids = selectedItems.map((item) => item.key);
    setRowTypeIds(ids);
    setReportSettingsHasChanges(true);
  }, []);

  const handleRecommendedActionChangeDropdown = useCallback((ids: number[]) => {
    setRecommendedActionIds(ids);
    setReportSettingsHasChanges(true);
  }, []);

  const handleShowAllReasonsChange = useCallback(() => {
    setShowAllRecommendedActionReasons(prev => !prev);
    setReportSettingsHasChanges(true);
  }, []);

  const handleRecommendedActionReasonDropdownChange = useCallback((items: Array<DropdownItem<string>>) => {
    setSelectedRecommendedActionReasons(items.map(item => item.key));
    setReportSettingsHasChanges(true);
  }, []);

  const WIZARD_SUPPORTED_SETTINGS_KEYS = ["entityTypeFilter", "recommendedActionIds", "rowTypeIds", "selectedRecommendedActionReasons", "showAllRecommendedActionReasons"];

  const hasUnsupportedSettings = useCallback((reportSettingsJson: string): boolean => {
    try {
      const settings = JSON.parse(reportSettingsJson);
      return Object.keys(settings).some(key => !WIZARD_SUPPORTED_SETTINGS_KEYS.includes(key));
    } catch {
      return false;
    }
  }, []);

    const parseReportSettingsJsonToState = useCallback((reportSettingsJson: string) => {
    try {
      const settings = JSON.parse(reportSettingsJson);
      if (settings.entityTypeFilter) {
        setEntityTypeFilter(settings.entityTypeFilter);
      }
      if (settings.recommendedActionIds) {
        setRecommendedActionIds(settings.recommendedActionIds);
      }
      if (settings.rowTypeIds) {
        setRowTypeIds(settings.rowTypeIds);
      }
      if (settings.selectedRecommendedActionReasons) {
        const availableReasons = recommendedActionReasons.map(r => r.reason);
        setSelectedRecommendedActionReasons(
          (settings.selectedRecommendedActionReasons as string[]).filter(r => availableReasons.includes(r))
        );
      }
      if (typeof settings.showAllRecommendedActionReasons === "boolean") {
        setShowAllRecommendedActionReasons(settings.showAllRecommendedActionReasons);
      }
      setShowSettingsPartialApplyWarning(hasUnsupportedSettings(reportSettingsJson));
      setIsSelectionValid(true);
    } catch {
      setIsSelectionValid(false);
    }
  }, [recommendedActionReasons, hasUnsupportedSettings]);

  const loadRecommendedActionReasons = useCallback(async (defaultSettings?: ReportSettingsModel) => {
    if (selectedProjects.length === 0) {
      setRecommendedActionReasons([]);
      setSelectedRecommendedActionReasons([]);
      recommendedActionReasonsLoadedRef.current = false;
      return;
    }

    if (recommendedActionReasonsLoadedRef.current) {
      return;
    }

    setLoadingRecommendedActionReasons(true);
    try {
      const projectIds = selectedProjects.map(p => p.customProjectId);
      const projectDetails = await projectService.getByIds(projectIds, true, false, 1);
      
      const allReasonsMap = new Map<string, ExportRecommendedActionReasonModel>();
      
      for (const project of projectDetails) {
        if (project.projectRunConfigurations && project.projectRunConfigurations.length > 0) {
          const runConfigId = project.projectRunConfigurations[0].projectRunConfigurationId;
          try {
            const reasons = await exportService.getExportAccuracyRecommendedActionReasons(
              project.projectId,
              runConfigId,
              []
            );
            reasons.forEach(r => {
              if (!allReasonsMap.has(r.reason)) {
                allReasonsMap.set(r.reason, r);
              }
            });
          } catch (err) {
            console.warn(`Failed to load reasons for project ${project.projectId}:`, err);
          }
        }
      }
      
      const allReasons = Array.from(allReasonsMap.values());
      const allReasonKeys = allReasons.map(r => r.reason);
      setRecommendedActionReasons(allReasons);
      
      const settingsToUse = defaultSettings || currentReportSettings;
      
      if (settingsToUse) {
        try {
          const settings = JSON.parse(settingsToUse.reportSettingsJson);
          setShowSettingsPartialApplyWarning(hasUnsupportedSettings(settingsToUse.reportSettingsJson));

          if (settings.entityTypeFilter) {
            setEntityTypeFilter(settings.entityTypeFilter);
          }
          
          if (settings.recommendedActionIds) {
            setRecommendedActionIds(settings.recommendedActionIds);
          }
          
          if (settings.rowTypeIds) {
            setRowTypeIds(settings.rowTypeIds);
          }
          
          if (typeof settings.showAllRecommendedActionReasons === "boolean") {
            setShowAllRecommendedActionReasons(settings.showAllRecommendedActionReasons);
          }
          
          if (settings.selectedRecommendedActionReasons) {
            const savedReasons = (settings.selectedRecommendedActionReasons as string[]).filter(r => allReasonKeys.includes(r));
            setSelectedRecommendedActionReasons(savedReasons);
          } else {
            setSelectedRecommendedActionReasons(allReasonKeys);
          }
        } catch {
          setSelectedRecommendedActionReasons(allReasonKeys);
        }
      } else {
        setSelectedRecommendedActionReasons(allReasonKeys);
        setShowSettingsPartialApplyWarning(false);
      }
      
      recommendedActionReasonsLoadedRef.current = true;
    } catch (err) {
      console.error("Error loading recommended action reasons:", err);
    } finally {
      setLoadingRecommendedActionReasons(false);
    }
  }, [selectedProjects, projectService, exportService, currentReportSettings, hasUnsupportedSettings]);

  const stringifyStateReportSettings = useCallback((): string => {
    const settings = {
      entityTypeFilter,
      recommendedActionIds,
      rowTypeIds,
      selectedRecommendedActionReasons,
      showAllRecommendedActionReasons,
    };
    return JSON.stringify(settings);
  }, [entityTypeFilter, recommendedActionIds, rowTypeIds, selectedRecommendedActionReasons, showAllRecommendedActionReasons]);

  const buildReportSettingsModel = useCallback((isDefault: boolean, name: string): ReportSettingsModel => {
    const reportSettingsJson = stringifyStateReportSettings();
    return {
      isDefault,
      name,
      reportSettingsJson,
      type: ReportSettingsType.AccuracyActionableProviders,
    } as ReportSettingsModel;
  }, [stringifyStateReportSettings]);

  const loadReportSettings = useCallback(async () => {
    try {
      const allSettings = await exportService.getAllUserReportSettings();
      const accuracySettings = allSettings.filter(s => s.type === ReportSettingsType.AccuracyActionableProviders);
      setReportSettingsList(accuracySettings);
      
      const defaultSettings = accuracySettings.find(s => s.isDefault);
      if (defaultSettings) {
        setCurrentReportSettings(defaultSettings);
      }

      return defaultSettings;
    } catch (err) {
      console.error("Error loading report settings:", err);
      return undefined;
    }
  }, [exportService]);

  const goToNextStep = useCallback(() => {
    switch (page) {
      case AccuracyReportsWizardContextSteps.SelectProjects:
        setPage(AccuracyReportsWizardContextSteps.SelectActionableProviders);
        break;
      case AccuracyReportsWizardContextSteps.SelectActionableProviders:
        setPage(AccuracyReportsWizardContextSteps.Configure);
        break;
      case AccuracyReportsWizardContextSteps.Configure:
        setProcessing(true);
        setTimeout(() => {
          setProcessing(false);
        }, 2000);
        break;
    }
  }, [page]);

  const goToPreviousStep = useCallback(() => {
    switch (page) {
      case AccuracyReportsWizardContextSteps.SelectActionableProviders:
        setPage(AccuracyReportsWizardContextSteps.SelectProjects);
        break;
      case AccuracyReportsWizardContextSteps.Configure:
        setPage(AccuracyReportsWizardContextSteps.SelectActionableProviders);
        break;
    }
  }, [page]);

  const goToStep = useCallback((step: AccuracyReportsWizardContextSteps) => {
    setPage(step);
  }, []);

  const refreshProjects = useCallback(() => {
    if (projects.length !== 0) {
      return;
    }
    setLoading(true);
    setError("");

    resourceFiltrationService.getAccuracyReportingProjects([], true, false)
      .then((projectResponse) => {
        const accuracyProjects: AccuracyProject[] = projectResponse.map((project) => ({
          customProjectId: project.customProjectId,
          isSelected: false,
          isVisible: true,
          name: project.projectName
        }));
        setProjects(accuracyProjects);
        setLoading(false);
      })
      .catch(() => {
        setError("We were unable to retrieve the filtered list of projects.");
        setLoading(false);
      });
  }, [projects, resourceFiltrationService]);

  const updateProjectVisibility = useCallback((filterString: string) => {
    const updatedProjects = projects.map((project) => ({
      ...project,
      isVisible: !filterString || project.name.toLowerCase().includes(filterString.toLowerCase()),
    }));
    setProjects(updatedProjects);
  }, [projects]);

  const getExportFormatType = (format: ReportFormatOptionsType): ExportFormatType => {
    switch (format) {
      case "CSV File":
        return ExportFormatType.CSV;
      case "Pipe-delimited Text":
        return ExportFormatType.Pipe;
      case "Tab-delimited Text":
        return ExportFormatType.Tab;
      default:
        return ExportFormatType.CSV;
    }
  };

  const getDefaultPractitionerAtLocationConfidenceIds = (): number[] => {
    const defaults: number[] = [];
    Object.values(LocationConfidenceScoresLabelEnum).forEach((_, i) => {
      defaults.push(i);
    });
    return defaults;
  };

  const getDefaultPcaLabelIds = (): number[] => {
    const defaults: number[] = [];
    Object.values(PatientVolumeLabelEnum).forEach((_, i) => {
      defaults.push(i);
    });
    return defaults;
  };

  const generateReports = useCallback(async () => {
    setProcessing(true);
    setError("");
    setSuccess(false);

    if (selectedProjects.length === 0) {
      setError("No projects selected for report generation.");
      setProcessing(false);
      return;
    }

    try {
      const formatType = getExportFormatType(reportFormat);
      
      // Fetch project details to get run configuration IDs
      const projectIds = selectedProjects.map(p => p.customProjectId);
      const projectDetails = await projectService.getByIds(projectIds, true, false, 1);
      
      // Create a map of project ID to latest run configuration ID
      const projectRunConfigMap = new Map<number, number>();
      projectDetails.forEach(project => {
        if (project.projectRunConfigurations && project.projectRunConfigurations.length > 0) {
          // Get the first (latest) run configuration
          projectRunConfigMap.set(project.projectId, project.projectRunConfigurations[0].projectRunConfigurationId);
        }
      });

      // Filter out projects without run configurations
      const validProjects = selectedProjects.filter(p => projectRunConfigMap.has(p.customProjectId));
      
      if (validProjects.length === 0) {
        setError("None of the selected projects have valid run configurations.");
        setProcessing(false);
        return;
      }

      // Determine if this is a scheduled report (One-Time only for now)
      const isScheduled = scheduleReportCreation && frequency === "One-Time" && startDate && startTime;
      const scheduledAt = isScheduled ? combineDateTimeToISO(startDate, startTime) : undefined;

      // For multiple projects, use bulk endpoint to create a single zip with all reports
      // For single project, still use the single endpoint
      if (validProjects.length > 1) {
        const firstRunConfigId = projectRunConfigMap.get(validProjects[0].customProjectId);

        const bulkModel: ExportAccuracyActionableProvidersBulkModel = {
          allowParallelization: true,
          customProjectIds: validProjects.map(p => p.customProjectId),
          eMailOnSuccess: alertWhenReady,
          exportName: reportName,
          filterByTrueCounty: true,
          formatType: formatType,
          includeActiveRegionSets: true,
          includePendingRegionSets: true,
          includePractitionerEntityType: entityTypeFilter.includePractitionerEntityType,
          includeServiceLocationEntityType: entityTypeFilter.includeServiceLocationEntityType,
          inspectionBand: "Unknown",
          inspectionUids: [] as string[],
          networkAssessmentAnalysisType: NetworkAssessmentAnalysisType.ServiceArea,
          pcaLabels: getDefaultPcaLabelIds(),
          practitionerAtLocationConfidenceScores: getDefaultPractitionerAtLocationConfidenceIds(),
          projectRunConfigurationId: firstRunConfigId!,
          projectURL: window.location.origin,
          providerIds: [] as number[],
          providerNetworkType: ProviderNetworkTypeEnum.Network,
          publishedOnly: true,
          recommendedActionReasons: selectedRecommendedActionReasons,
          recommendedActions: recommendedActionIds,
          rowTypes: rowTypeIds,
          runAsynchronous: true,
          separateReportOutput: true,
          sharedUsers: sharedUsers,
          showAllRecommendedActionReasons: showAllRecommendedActionReasons,
          specialtyGroupCodes: [] as string[],
          ...(scheduledAt && { scheduledAt }),
        };

        await exportService.postExportAccuracyActionableProvidersBulk(bulkModel);
      } else {
        // Single project - use original endpoint
        const project = validProjects[0];
        const runConfigId = projectRunConfigMap.get(project.customProjectId);

        const actionableProvidersModel = {
          customProjectId: project.customProjectId,
          eMailOnSuccess: alertWhenReady,
          exportName: reportName,
          filterByTrueCounty: true,
          formatType: formatType,
          includeActiveRegionSets: true,
          includePendingRegionSets: true,
          includePractitionerEntityType: entityTypeFilter.includePractitionerEntityType,
          includeServiceLocationEntityType: entityTypeFilter.includeServiceLocationEntityType,
          inspectionBand: "Unknown",
          inspectionUids: [] as string[],
          networkAssessmentAnalysisType: NetworkAssessmentAnalysisType.ServiceArea,
          pcaLabels: getDefaultPcaLabelIds(),
          practitionerAtLocationConfidenceScores: getDefaultPractitionerAtLocationConfidenceIds(),
          projectRunConfigurationId: runConfigId,
          projectURL: window.location.origin,
          providerIds: [] as number[],
          providerNetworkType: ProviderNetworkTypeEnum.Network,
          recommendedActionReasons: selectedRecommendedActionReasons,
          recommendedActions: recommendedActionIds,
          rowTypes: rowTypeIds,
          runAsynchronous: true,
          sharedUsers: sharedUsers,
          showAllRecommendedActionReasons: showAllRecommendedActionReasons,
          specialtyGroupCodes: [] as string[],
          ...(scheduledAt && { scheduledAt }),
        };

        await exportService.postExportAccuracyActionableProviders(actionableProvidersModel);
      }
      
      const skippedCount = selectedProjects.length - validProjects.length;
      let message = isScheduled 
        ? "Report Submitted Successfully"
        : `${validProjects.length} report(s) submitted successfully. You will be notified when they are ready.`;
      if (skippedCount > 0 && !isScheduled) {
        message += ` ${skippedCount} project(s) were skipped due to missing run configurations.`;
      }
      
      setSuccess(true);
      toast.success(message, {
        containerId: "z-index-1030"
      });
    } catch (err) {
      setError("Failed to generate reports. Please try again.");
      console.error("Error generating reports:", err);
    } finally {
      setProcessing(false);
    }
  }, [selectedProjects, reportName, reportFormat, alertWhenReady, sharedUsers, scheduleReportCreation, frequency, startDate, startTime, exportService, projectService, entityTypeFilter, rowTypeIds, recommendedActionIds, selectedRecommendedActionReasons, showAllRecommendedActionReasons]);

  useEffect(() => {
    refreshProjects();
  }, [refreshProjects]);

  useEffect(() => {
    const loadData = async () => {
      if (page === AccuracyReportsWizardContextSteps.SelectActionableProviders && selectedProjects.length > 0) {
        const defaultSettings = await loadReportSettings();
        if (defaultSettings) {
          setShowSettingsPartialApplyWarning(hasUnsupportedSettings(defaultSettings.reportSettingsJson));
        }
        await loadRecommendedActionReasons(defaultSettings);
      }
    };
    loadData();
  }, [page, selectedProjects]);

  useEffect(() => {
    recommendedActionReasonsLoadedRef.current = false;
  }, [selectedProjects]);

  return (
    <AccuracyReportsContext.Provider
      value={{
        alertWhenReady,
        buildReportSettingsModel,
        createReportSettingsNameInputDisabled,
        currentReportSettings,
        entityTypeFilter,
        error,
        frequency,
        generateReports,
        goToNextStep,
        goToPreviousStep,
        goToStep,
        handleEntityTypeIncludeChangeDropdown,
        handleRecommendedActionChangeDropdown,
        handleRecommendedActionReasonDropdownChange,
        handleRowTypeChangeDropdown,
        handleShowAllReasonsChange,
        isSelectionValid,
        loading,
        loadingRecommendedActionReasons,
        loadRecommendedActionReasons,
        loadReportSettings,
        page,
        parseReportSettingsJsonToState,
        processing,
        projects,
        recommendedActionIds,
        recommendedActionReasons,
        refreshProjects,
        reportFormat,
        reportName,
        reportSettingsHasChanges,
        reportSettingsHasInvalidChanges,
        reportSettingsList,
        reportType,
        rowTypeIds,
        saveClicked,
        scheduleReportCreation,
        selectedProjects,
        selectedRecommendedActionReasons,
        setAlertWhenReady,
        setCreateReportSettingsNameInputDisabled,
        setCurrentReportSettings,
        setEntityTypeFilter,
        setError,
        setFrequency,
        setIsSelectionValid,
        setProjects,
        setRecommendedActionIds,
        setReportFormat,
        setReportName,
        setReportSettingsHasChanges,
        setReportSettingsHasInvalidChanges,
        setReportSettingsList,
        setRowTypeIds,
        setSaveClicked,
        setScheduleReportCreation,
        setSelectedProjects,
        setSelectedRecommendedActionReasons,
        setSharedUsers,
        setShowAllRecommendedActionReasons,
        setShowSettingsPartialApplyWarning,
        setSplitReport,
        setStartDate,
        setStartTime,
        sharedUsers,
        showAllRecommendedActionReasons,
        showSettingsPartialApplyWarning,
        splitReport,
        startDate,
        startTime,
        stringifyStateReportSettings,
        success,
        updateProjectVisibility,
      }}
    >
      {children}
    </AccuracyReportsContext.Provider>
  );
};